import { useEffect, useState } from "react";
import {
  Button, Typography, TextField, Box, Stack, Fade, IconButton,
  RadioGroup, FormControlLabel, Radio
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import API from "../api/api";
import GlobalSkeleton from "../components/GlobalSkeleton";
import { useLocation, useNavigate } from "react-router-dom";
import MapPicker from "./MapPicker";

export default function LocationPage({ isDrawer = true, onSuccess, onCancel }) {
  const navigate = useNavigate(); 
  const location = useLocation();
  const editAddress = location.state?.editAddress || null;

  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(!editAddress); // Skip detection if editing
  const [coords, setCoords] = useState(editAddress ? { lat: editAddress.lat, lng: editAddress.lng } : null);

  const [step, setStep] = useState(editAddress ? 2 : 1); // Jump to Step 2 if editing
  const [mapData, setMapData] = useState(editAddress ? { formatted_address: editAddress.formatted_address || "Saved Location" } : null);

  const [orderingFor, setOrderingFor] = useState("myself");
  const [receiverName, setReceiverName] = useState("");
  const [receiverPhone, setReceiverPhone] = useState("");

  const [form, setForm] = useState({
    flat: editAddress?.flat || "",
    building: editAddress?.building || "",
    landmark: editAddress?.landmark || ""
  });

  const [type, setType] = useState(editAddress?.address_type || "HOME");

  useEffect(() => {
    if (editAddress) return; // Do not fetch GPS if we are editing an existing address

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setDetecting(false);
      },
      (error) => {
        setDetecting(false);
        if (error.code === error.PERMISSION_DENIED) {
          alert("Location access denied. Please manually drag the map to your location.");
        } else {
          alert("Could not automatically detect location. Please place the pin manually.");
        }
      },
      { enableHighAccuracy: true }
    );
  }, [editAddress]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleMapConfirm = (data) => {
    setMapData(data);
    setCoords({ lat: data.lat, lng: data.lng });
    setStep(2);
  };

  const handleSave = async () => {
    if (document.activeElement) document.activeElement.blur(); 

    if (!form.flat || !form.building) {
      alert("Please fill required fields");
      return;
    }

    if (orderingFor === "someone_else" && (!receiverName || !receiverPhone)) {
      alert("Please provide the receiver's name and mobile number.");
      return;
    }

    if (!coords) {
      alert("Location not detected");
      return;
    }

    const userStr = localStorage.getItem("user");
    if (!userStr) {
      alert("Please login first");
      return;
    }
    const parsedUser = JSON.parse(userStr);

    try {
      setLoading(true);

      if (editAddress) {
        // === UPDATE EXISTING ADDRESS ===
        await API.put(`/user/address/${editAddress.id}`, {
          flat: form.flat,
          building: form.building,
          landmark: form.landmark,
          address_type: type
        });
        
        localStorage.setItem("addressUpdated", Date.now());
        if (onSuccess) {
          onSuccess(editAddress);
        } else {
          navigate("/profile"); // Redirect back to account page
        }

      } else {
        // === CREATE NEW ADDRESS ===
        const check = await API.post("/check-location", {
          lat: coords.lat,
          lng: coords.lng
        });

        if (!check.data.serviceable) {
          alert(check.data.message);
          return;
        }

        const res = await API.post("/user/address", {
          flat: form.flat,
          building: form.building,
          landmark: form.landmark,
          lat: coords.lat,
          lng: coords.lng,
          address_type: type,
          frontend_ip: null, // Backend securely handles this now
          formatted_address: mapData?.formatted_address || "Unknown Location",
          receiver_name: orderingFor === "someone_else" ? receiverName : parsedUser.name,
          receiver_phone: orderingFor === "someone_else" ? receiverPhone : parsedUser.phone
        });

        await API.put(`/user/address/${res.data.id}/set-default`);
        localStorage.setItem("addressUpdated", Date.now());
        
        if (onSuccess) {
          onSuccess(res.data);
        } else {
          navigate("/profile"); // Redirect back to account page
        }
      }

    } catch (err) {
      console.error(err);
      const backendError = err.response?.data?.detail || err.response?.data?.error || "Something went wrong while saving.";
      alert(backendError);
    } finally {
      setLoading(false);
    }
  };

  const colorfulInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px', backgroundColor: '#FFFFFF', transition: 'all 0.3s ease',
      '& fieldset': { borderColor: '#ECE5DA' },
      '&:hover fieldset': { borderColor: '#F29D17' },
      '&.Mui-focused': {
        backgroundColor: '#FFFFFF', boxShadow: '0 4px 16px rgba(242, 157, 23, 0.1)'
      },
      '&.Mui-focused fieldset': { borderColor: '#F29D17', borderWidth: '2px' },
      '& input, & .MuiSelect-select': { color: '#2F2A25', fontWeight: 600 }
    },
    '& .MuiInputLabel-root': { color: '#A59D96', fontWeight: 500 },
    '& .MuiInputLabel-root.Mui-focused': { color: '#F29D17', fontWeight: 700 }
  };

  if (detecting) {
    return (
      <Box sx={{ p: isDrawer ? 2 : 3, maxWidth: '600px', mx: 'auto', bgcolor: '#FFFDF8', borderRadius: isDrawer ? '24px 24px 0 0' : '24px', height: '100%' }}>
        <Fade in={detecting}>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ textAlign: "center", mb: 4, mt: 2 }}>
              <Typography variant="h6" fontWeight="800" sx={{ color: '#3A2C23' }}>Locating you...</Typography>
              <Typography variant="body2" sx={{ color: '#6E655E', mt: 0.5, fontWeight: 500 }}>Ensuring precise delivery to your doorstep</Typography>
            </Box>
            <GlobalSkeleton type="default" />
          </Box>
        </Fade>
      </Box>
    );
  }

  // RENDER STEP 1: MAP PICKER
  if (step === 1 && !detecting) {
    return (
      <Box sx={{ 
        maxWidth: '600px', 
        mx: 'auto', 
        width: '100%', 
        height: '100%', 
        minHeight: 'calc(100dvh - 140px)', // 👈 FIX: Matched dynamic viewport height 
        flexGrow: 1,
        position: 'relative', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: '0', 
        overflow: 'hidden'
      }}>
        <MapPicker 
          initialCoords={coords} 
          onConfirm={handleMapConfirm} 
          onCancel={() => { if (onCancel) onCancel(); else navigate(-1); }}
        />
      </Box>
    );
  }

  // RENDER STEP 2: ADDRESS & RECEIVER DETAILS
  return (
    <Box sx={{ 
      p: isDrawer ? 3 : 3, 
      maxWidth: '600px', 
      mx: 'auto',
      bgcolor: '#FFFDF8',
      borderRadius: isDrawer ? '24px 24px 0 0' : '24px',
      height: '100%',
      overflowY: 'auto' 
    }}>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton 
          onClick={() => {
            if (editAddress) navigate(-1); // Go back to profile if editing
            else setStep(1); // Go back to map
          }}
          sx={{ 
            mr: 2, color: '#3A2C23', bgcolor: '#eadbb7', width: 44, height: 44,
            border: '3px solid #FFFFFF', boxShadow: '0 4px 10px rgba(58, 44, 35, 0.05)',
            '&:hover': { bgcolor: '#e0ceab' } 
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 22 }} />
        </IconButton>
        <Typography variant="h6" fontWeight="800" sx={{ color: '#3A2C23' }}>
          Enter address details
        </Typography>
      </Box>

      <Fade in={!detecting} timeout={500}>
        <Box>
          <Stack spacing={3}>
            
            <Box>
              <Typography variant="body2" fontWeight="700" color="#6E655E" mb={1}>
                Who are you ordering for?
              </Typography>
              <RadioGroup row value={orderingFor} onChange={(e) => setOrderingFor(e.target.value)}>
                <FormControlLabel 
                  value="myself" 
                  control={<Radio sx={{ color: '#6A9E3A', '&.Mui-checked': { color: '#6A9E3A' } }} />} 
                  label={<Typography fontWeight="700" color="#3A2C23">Myself</Typography>} 
                />
                <FormControlLabel 
                  value="someone_else" 
                  control={<Radio sx={{ color: '#6A9E3A', '&.Mui-checked': { color: '#6A9E3A' } }} />} 
                  label={<Typography fontWeight="700" color="#3A2C23">Someone else</Typography>} 
                />
              </RadioGroup>
            </Box>

            {orderingFor === "someone_else" && (
              <Box sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #ECE5DA', borderRadius: '16px' }}>
                <Typography variant="subtitle2" fontWeight="800" mb={2} color="#3A2C23">
                  Receiver's details
                </Typography>
                <Stack spacing={2}>
                  <TextField label="Receiver's Name *" value={receiverName} onChange={(e) => setReceiverName(e.target.value)} fullWidth sx={colorfulInputSx} />
                  <TextField label="Receiver's Mobile Number *" value={receiverPhone} onChange={(e) => setReceiverPhone(e.target.value)} fullWidth sx={colorfulInputSx} />
                </Stack>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'flex-start', bgcolor: '#f5f0e6', p: 2, borderRadius: '16px', border: '1px solid #eadbb7' }}>
              <LocationOnIcon sx={{ color: '#E85D3F', mr: 1.5, mt: 0.2 }} />
              <Box sx={{ flex: 1, pr: 1 }}>
                <Typography variant="caption" color="#A59D96" fontWeight="800" textTransform="uppercase">
                  Area / Locality
                </Typography>
                <Typography variant="body2" color="#3A2C23" fontWeight="600" sx={{ mt: 0.5, lineHeight: 1.4 }}>
                  {mapData?.formatted_address || "Location fetched from map"}
                </Typography>
              </Box>
              {!editAddress && (
                <Button size="small" onClick={() => setStep(1)} sx={{ color: '#E85D3F', fontWeight: '800', textTransform: 'none', mt: -0.5 }}>
                  Change
                </Button>
              )}
            </Box>

            <Box>
              <Typography variant="body2" fontWeight="700" color="#6E655E" mb={1.5}>
                Save address as
              </Typography>
              <Stack direction="row" spacing={1.5} mb={3}>
                {['Home', 'Work', 'Other'].map((pill) => {
                  const val = pill.toUpperCase();
                  return (
                    <Button
                      key={pill} 
                      onClick={() => setType(val)}
                      sx={{
                        borderRadius: '12px', py: 0.8, px: 2, textTransform: 'none', fontWeight: '700',
                        bgcolor: type === val ? '#F8C332' : '#FFFFFF',
                        color: type === val ? '#3A2C23' : '#6E655E',
                        border: type === val ? '1px solid #F8C332' : '1px solid #ECE5DA',
                        boxShadow: type === val ? '0 4px 12px rgba(248, 195, 50, 0.3)' : 'none',
                        '&:hover': { bgcolor: type === val ? '#F5B71D' : '#F2EEE8' }
                      }}
                    >
                      {pill}
                    </Button>
                  );
                })}
              </Stack>
              
              <TextField label="Flat / House No *" name="flat" value={form.flat} onChange={handleChange} fullWidth sx={{ ...colorfulInputSx, mb: 2.5 }} />
              <TextField label="Building / Floor / Street *" name="building" value={form.building} onChange={handleChange} fullWidth sx={{ ...colorfulInputSx, mb: 2.5 }} />
              <TextField label="Landmark (optional)" name="landmark" value={form.landmark} onChange={handleChange} fullWidth sx={colorfulInputSx} />
            </Box>

            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleSave}
              disabled={loading}
              sx={{ 
                mt: 3, mb: 4, py: 1.8, borderRadius: '16px', bgcolor: '#F8C332', color: '#3A2C23',
                fontWeight: '800', textTransform: 'none', fontSize: '1.05rem',
                boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)', transition: 'all 0.2s ease-in-out',
                '&:hover': { bgcolor: '#F5B71D', transform: 'translateY(-2px)' },
                '&:active': { transform: 'translateY(1px)' },
                '&.Mui-disabled': { bgcolor: '#F2EEE8', color: '#A59D96', boxShadow: 'none' }
              }}
            >
              {loading 
                ? (editAddress ? "Updating Address..." : "Checking Delivery Zone...") 
                : (editAddress ? "Update Address" : "Save Address & Proceed")}
            </Button>
          </Stack>
        </Box>
      </Fade>
    </Box>
  );
}